import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, ErrorBanner, Field, Input, PasswordInput } from "@/components/ui-kit";
import { ExpressRateScheduleEditor } from "@/components/ExpressRateScheduleEditor";
import { formatNaira, useAuth } from "@/lib/auth";
import type { ExpressRateBand } from "@/lib/express-rates";
import { supabase } from "@/integrations/supabase/client";
import {
  approveKyc,
  createKycDocSignedUrl,
  declineKyc,
  fetchAllBorrowers,
  fetchLoansForBorrower,
  grantElite,
  setBorrowerMembership,
  unlockAndReplaceBankStatement,
  updateBorrowerProfile,
  upsertBorrowerSettings,
} from "@/lib/data";
import { invokeFunction } from "@/lib/invoke";
import {
  KYC_DOC_LABELS,
  KYC_IMAGE_TYPES,
  KYC_PDF_TYPES,
  TIER_LABEL,
  formatDurationDays,
  isEliteReady,
  isVerifiedUpgradePending,
  type Borrower,
  type KycContextAnalysis,
  type KycDocument,
  type Loan,
  type VerificationLevel,
} from "@/lib/types";

export const Route = createFileRoute("/admin/borrowers")({
  component: BorrowersPage,
});

function BorrowersPage() {
  const { session } = useAuth();
  const [borrowers, setBorrowers] = useState<Borrower[]>([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "declined" | "not_started">("all");
  const [active, setActive] = useState<Borrower | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const rows = await fetchAllBorrowers();
    setBorrowers(rows);
    if (active) {
      const fresh = rows.find((b) => b.id === active.id) ?? null;
      if (fresh) setActive(fresh);
    }
  }, [active]);

  useEffect(() => {
    refresh().catch((e) => setError(e instanceof Error ? e.message : "Failed to load borrowers"));
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel("kyc-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "kyc" }, () => { void refresh(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [refresh]);

  const filtered = borrowers.filter((b) => {
    if (filter !== "all" && b.kycStatus !== filter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return b.name.toLowerCase().includes(q) || b.email.toLowerCase().includes(q) || b.phone.includes(q);
  });

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Operations</div>
          <h1 className="font-display text-4xl mt-1">Borrowers</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(["all", "pending", "approved", "not_started", "declined"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider border transition ${
                filter === f ? "border-gold text-gold bg-gold/10" : "border-border text-muted-foreground hover:border-gold/40"
              }`}
            >
              {f.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      <ErrorBanner message={error} />

      <Card className="!p-4">
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, phone..." />
      </Card>

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-accent/30">
                <th className="py-3 px-4">Borrower</th>
                <th className="py-3 px-4">Contact</th>
                <th className="py-3 px-4">KYC</th>
                <th className="py-3 px-4">Limit</th>
                <th className="py-3 px-4">Score</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-b border-border/40 hover:bg-accent/20">
                  <td className="py-3 px-4">
                    <div className="font-medium">{b.name}</div>
                    <div className="text-xs text-muted-foreground">{b.email}</div>
                    <div className="text-xs text-muted-foreground">Joined {new Date(b.createdAt).toLocaleDateString()}</div>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">
                    <div>{b.phone || "—"}</div>
                  </td>
                  <td className="py-3 px-4 space-x-1.5 whitespace-nowrap">
                    <Badge tone={kycTone(b.kycStatus)}>{b.kycStatus.replace("_", " ")}</Badge>
                    {b.verificationLevel !== "none" && <Badge tone="gold">{TIER_LABEL[b.verificationLevel]}</Badge>}
                    {isVerifiedUpgradePending(b) && (
                      <Badge tone="warning">Upgrade to Verified · pending</Badge>
                    )}
                    {isEliteReady(b) && <Badge tone="warning">Elite-ready</Badge>}
                  </td>
                  <td className="py-3 px-4">{formatNaira(b.loanLimit)}</td>
                  <td className="py-3 px-4">{b.creditScore || "—"}</td>
                  <td className="py-3 px-4 text-right">
                    <Button variant="outline" onClick={() => setActive(b)}>Review</Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">No borrowers match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {active && (
        <BorrowerDrawer
          borrower={active}
          isSuper={session?.kind === "admin" && session.role === "super"}
          onClose={() => setActive(null)}
          onChange={refresh}
        />
      )}
    </div>
  );
}

function kycTone(s: Borrower["kycStatus"]) {
  return s === "approved" ? "success" : s === "pending" ? "warning" : s === "declined" ? "danger" : "neutral";
}

function parseContext(raw: unknown): KycContextAnalysis | null {
  if (!raw || typeof raw !== "object") return null;
  return raw as KycContextAnalysis;
}

function riskTone(level?: string) {
  const l = (level || "").toLowerCase();
  if (l === "low") return "success" as const;
  if (l === "high") return "danger" as const;
  return "warning" as const;
}

function BorrowerDrawer({ borrower, isSuper, onClose, onChange }: {
  borrower: Borrower;
  isSuper: boolean;
  onClose: () => void;
  onChange: () => Promise<void>;
}) {
  const [limit, setLimit] = useState(String(borrower.loanLimit));
  const [fullNameDraft, setFullNameDraft] = useState(borrower.name);
  const [terms, setTerms] = useState(borrower.terms);
  const [customExpressSchedule, setCustomExpressSchedule] = useState(
    Boolean(borrower.terms.expressRateScheduleOverride?.length),
  );
  const [expressScheduleDraft, setExpressScheduleDraft] = useState<ExpressRateBand[]>(
    borrower.terms.expressRateScheduleOverride ?? borrower.terms.expressRateSchedule,
  );
  const [error, setError] = useState<string | null>(null);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [declineReason, setDeclineReason] = useState("");
  const [approveLevel, setApproveLevel] = useState<"express" | "verified">(
    borrower.kyc?.verificationLevel === "verified" || borrower.verificationLevel === "verified" ? "verified" : "express",
  );
  const [membershipDraft, setMembershipDraft] = useState<VerificationLevel>(
    borrower.verificationLevel === "none" ? "express" : borrower.verificationLevel,
  );
  const [revokeReason, setRevokeReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [statementPdfPassword, setStatementPdfPassword] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [docUrls, setDocUrls] = useState<Record<string, { url: string | null; error?: string }>>({});

  useEffect(() => {
    setLimit(String(borrower.loanLimit));
    setFullNameDraft(borrower.name);
    setTerms(borrower.terms);
    setCustomExpressSchedule(Boolean(borrower.terms.expressRateScheduleOverride?.length));
    setExpressScheduleDraft(
      borrower.terms.expressRateScheduleOverride ?? borrower.terms.expressRateSchedule,
    );
    setApproveLevel(
      borrower.kyc?.verificationLevel === "verified" || borrower.verificationLevel === "verified" ? "verified" : "express",
    );
    setMembershipDraft(
      borrower.verificationLevel === "none" ? "express" : borrower.verificationLevel,
    );
    setRevokeReason("");
    fetchLoansForBorrower(borrower.id).then(setLoans).catch(() => setLoans([]));
  }, [borrower]);

  useEffect(() => {
    const docs = borrower.kyc?.documents ?? [];
    if (docs.length === 0) {
      setDocUrls({});
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        docs.map(async (d) => {
          const res = await createKycDocSignedUrl(d.storagePath);
          return [d.storagePath, res] as const;
        }),
      );
      if (!cancelled) {
        setDocUrls(Object.fromEntries(entries));
      }
    })();
    return () => { cancelled = true; };
  }, [borrower.kyc?.documents]);

  const setStatus = async (status: "approved" | "declined") => {
    if (!borrower.kycId) return setError("No KYC record found.");
    setBusy(true);
    setError(null);
    try {
      if (status === "approved") await approveKyc(borrower.id, borrower.kycId, approveLevel);
      else await declineKyc(borrower.id, borrower.kycId, declineReason);
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : "KYC update failed.");
    } finally {
      setBusy(false);
    }
  };

  const saveLimit = async () => {
    const n = Number(limit);
    if (Number.isNaN(n) || n < 0) return setError("Enter a valid limit.");
    setBusy(true);
    try {
      await upsertBorrowerSettings(borrower.id, { loanLimit: n });
      setError(null);
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save limit.");
    } finally {
      setBusy(false);
    }
  };

  const saveTerms = async () => {
    setBusy(true);
    setError(null);
    try {
      await upsertBorrowerSettings(borrower.id, {
        terms: {
          ...terms,
          expressRateScheduleOverride: customExpressSchedule ? expressScheduleDraft : null,
        },
      });
      await onChange();
    } catch (e) {
      const msg =
        e && typeof e === "object" && "message" in e && (e as { message?: unknown }).message
          ? String((e as { message: unknown }).message)
          : e instanceof Error
            ? e.message
            : "Failed to save terms.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const raw = borrower.kyc?.aiAnalysisRaw as Record<string, unknown> | undefined;
  const riskFlags = Array.isArray(raw?.risk_flags) ? (raw.risk_flags as string[]) : [];
  const positives = Array.isArray(raw?.positive_indicators) ? (raw.positive_indicators as string[]) : [];
  const riskAssessment = raw?.risk_assessment != null ? String(raw.risk_assessment) : undefined;
  const avgMonthlyIncome = raw?.average_monthly_income != null ? Number(raw.average_monthly_income) : undefined;
  const avgMonthlyExpenses = raw?.average_monthly_expenses != null ? Number(raw.average_monthly_expenses) : undefined;
  const incomeConsistency = raw?.income_consistency != null ? String(raw.income_consistency) : undefined;
  const loanRepaymentsDetected = typeof raw?.loan_repayments_detected === "boolean" ? raw.loan_repayments_detected : undefined;
  const gamblingDetected = typeof raw?.gambling_transactions_detected === "boolean" ? raw.gambling_transactions_detected : undefined;
  const periodStart = raw?.statement_period_start != null ? String(raw.statement_period_start) : undefined;
  const periodEnd = raw?.statement_period_end != null ? String(raw.statement_period_end) : undefined;
  const monthsCovered = raw?.months_covered != null ? Number(raw.months_covered) : undefined;
  const periodConfidence = raw?.period_confidence != null ? String(raw.period_confidence) : undefined;
  const periodNotes = raw?.period_notes != null ? String(raw.period_notes) : undefined;
  const pdfPagesSent = raw?.pdf_pages_sent != null ? Number(raw.pdf_pages_sent) : undefined;
  const hasAiAnalysis = Boolean(
    borrower.kyc?.aiSummary
    || borrower.kyc?.aiSuggestedLimit != null
    || borrower.kyc?.aiRiskScore != null
    || raw,
  );

  const ctx = parseContext(borrower.kyc?.aiKycContextRaw);
  const hasKycContext = Boolean(borrower.kyc?.aiKycContextSummary || ctx);
  const docs = borrower.kyc?.documents ?? [];
  const kyc = borrower.kyc;
  const bankStatementPath = docs.find((d) => d.type === "bank_statement")?.storagePath;

  const rerunAi = async () => {
    if (!borrower.kycId) return setError("No KYC record to analyze.");
    if (!bankStatementPath) return setError("No bank statement document on file.");
    setAiBusy(true);
    setError(null);
    try {
      let path = bankStatementPath;
      const pw = statementPdfPassword.trim();
      if (pw) {
        const replaced = await unlockAndReplaceBankStatement({
          userId: borrower.id,
          kycId: borrower.kycId,
          storagePath: bankStatementPath,
          password: pw,
        });
        path = replaced.path;
        setStatementPdfPassword("");
      }
      await invokeFunction("analyze-bank-statement", {
        kyc_id: borrower.kycId,
        storage_path: path,
      });
      await invokeFunction("analyze-kyc-context", { kyc_id: borrower.kycId });
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : "AI analysis failed.");
    } finally {
      setAiBusy(false);
    }
  };

  const socials = [
    kyc?.socialInstagram && { label: "Instagram", value: kyc.socialInstagram },
    kyc?.socialFacebook && { label: "Facebook", value: kyc.socialFacebook },
    kyc?.socialTwitter && { label: "Twitter / X", value: kyc.socialTwitter },
    kyc?.socialYoutube && { label: "YouTube", value: kyc.socialYoutube },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-navy-deep/70 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-xl bg-card border-l border-border overflow-y-auto p-6 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Borrower</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <h2 className="font-display text-2xl">{borrower.name}</h2>
              {borrower.verificationLevel !== "none" && (
                <Badge tone="gold">{TIER_LABEL[borrower.verificationLevel]}</Badge>
              )}
              {isVerifiedUpgradePending(borrower) && (
                <Badge tone="warning">Upgrade to Verified · pending</Badge>
              )}
              {isEliteReady(borrower) && <Badge tone="warning">Elite-ready</Badge>}
            </div>
            <div className="text-sm text-muted-foreground">{borrower.email} · {borrower.phone}</div>
            <div className="text-xs text-muted-foreground mt-1">Email {borrower.emailVerified ? "verified ✓" : "unverified"}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">×</button>
        </div>

        {isSuper && (
          <Card>
            <h3 className="font-display text-lg">Profile name</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Shown on loans and borrowers. Use full legal name (first + surname). Email stays as signup email.
            </p>
            <div className="mt-3 space-y-3">
              <Field label="Full name">
                <Input
                  value={fullNameDraft}
                  onChange={(e) => setFullNameDraft(e.target.value)}
                  placeholder="e.g. Charles Ofojetu"
                />
              </Field>
              <div className="text-xs text-muted-foreground">Email: {borrower.email}</div>
              <Button
                variant="outline"
                loading={busy}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await updateBorrowerProfile(borrower.id, { fullName: fullNameDraft });
                    await onChange();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : "Could not update name.");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Save full name
              </Button>
            </div>
          </Card>
        )}

        <Card>
          <h3 className="font-display text-lg">KYC documents</h3>
          <ErrorBanner message={error} />
          {kyc ? (
            <>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                {kyc.bvn && <Field label="BVN"><div className="text-foreground">{kyc.bvn}</div></Field>}
                {kyc.nin && <Field label="NIN"><div className="text-foreground">{kyc.nin}</div></Field>}
                {kyc.submittedAt && (
                  <Field label="Submitted">
                    <div className="text-xs">{new Date(kyc.submittedAt).toLocaleString()}</div>
                  </Field>
                )}
              </dl>

              {docs.length > 0 ? (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {docs.map((d) => (
                    <DocPreview key={`${d.type}-${d.storagePath}`} doc={d} signed={docUrls[d.storagePath]} />
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">No documents on file.</p>
              )}
            </>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">No KYC submitted yet.</p>
          )}

          {hasAiAnalysis && (
            <div className="mt-4 rounded-md border border-gold/30 bg-gold/5 p-3 text-xs space-y-2">
              <div className="uppercase tracking-wider text-gold mb-1">AI Bank Statement Analysis</div>
              {(periodStart || periodEnd || monthsCovered != null || periodNotes) && (
                <div className="rounded-md border border-border/50 bg-background/40 p-2 space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Statement period analyzed</div>
                  {(periodStart || periodEnd) && (
                    <div>
                      Range:{" "}
                      <span className="text-foreground">
                        {periodStart && periodStart !== "null" ? periodStart : "?"}
                        {" → "}
                        {periodEnd && periodEnd !== "null" ? periodEnd : "?"}
                      </span>
                    </div>
                  )}
                  {monthsCovered != null && Number.isFinite(monthsCovered) && (
                    <div>
                      Months covered: <span className="text-foreground">{monthsCovered}</span>
                      {periodConfidence ? (
                        <span className="text-muted-foreground"> · confidence: {periodConfidence}</span>
                      ) : null}
                    </div>
                  )}
                  {pdfPagesSent != null && Number.isFinite(pdfPagesSent) && (
                    <div className="text-muted-foreground">PDF pages sent to AI: {pdfPagesSent}</div>
                  )}
                  {periodNotes && <p className="text-muted-foreground">{periodNotes}</p>}
                </div>
              )}
              {kyc?.aiSuggestedLimit != null && (
                <div>Suggested limit: <span className="text-foreground">{formatNaira(kyc.aiSuggestedLimit)}</span></div>
              )}
              {kyc?.aiRiskScore != null && (
                <div>Risk score: <span className="text-foreground">{kyc.aiRiskScore}</span></div>
              )}
              {riskAssessment && <div>Risk assessment: <span className="text-muted-foreground">{riskAssessment}</span></div>}
              {kyc?.aiSummary && <p className="text-muted-foreground">{kyc.aiSummary}</p>}
              {avgMonthlyIncome != null && Number.isFinite(avgMonthlyIncome) && (
                <div>Average Monthly Income: <span className="text-foreground">{formatNaira(avgMonthlyIncome)}</span></div>
              )}
              {avgMonthlyExpenses != null && Number.isFinite(avgMonthlyExpenses) && (
                <div>Average Monthly Expenses: <span className="text-foreground">{formatNaira(avgMonthlyExpenses)}</span></div>
              )}
              {incomeConsistency && (
                <div>Income Consistency: <span className="text-muted-foreground">{incomeConsistency}</span></div>
              )}
              {loanRepaymentsDetected != null && (
                <div>Existing Loan Repayments: <span className="text-muted-foreground">{loanRepaymentsDetected ? "Yes" : "No"}</span></div>
              )}
              {gamblingDetected != null && (
                <div>
                  Gambling Transactions:{" "}
                  <span className={gamblingDetected ? "text-destructive" : "text-muted-foreground"}>
                    {gamblingDetected ? "Yes" : "No"}
                  </span>
                </div>
              )}
              {riskFlags.length > 0 && (
                <div>
                  <div className="text-destructive mb-1">Risk flags</div>
                  <ul className="list-disc pl-4 text-muted-foreground">{riskFlags.map((f) => <li key={f}>{f}</li>)}</ul>
                </div>
              )}
              {positives.length > 0 && (
                <div>
                  <div className="text-success mb-1">Positive indicators</div>
                  <ul className="list-disc pl-4 text-muted-foreground">{positives.map((f) => <li key={f}>{f}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          {borrower.kycId && bankStatementPath && (
            <div className="mt-3 space-y-2">
              <Field
                label="Statement PDF password (if locked)"
                hint="Only needed when the stored PDF is still encrypted. Unlock happens on your device; password is not saved."
              >
                <PasswordInput
                  autoComplete="off"
                  value={statementPdfPassword}
                  onChange={(e) => setStatementPdfPassword(e.target.value)}
                  placeholder="Optional — Access / bank PDF password"
                />
              </Field>
              <Button variant="outline" loading={aiBusy} onClick={() => void rerunAi()}>
                Re-run AI analysis
              </Button>
              <p className="mt-1 text-[10px] text-muted-foreground">
                If a password is entered, unlocks the statement first, then runs bank-statement AI and KYC context AI.
              </p>
            </div>
          )}

          {hasKycContext && (
            <div className="mt-4 rounded-md border border-gold/30 bg-gold/5 p-3 text-xs space-y-2">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="uppercase tracking-wider text-gold">KYC Context Analysis</div>
                {ctx?.context_risk_level && (
                  <Badge tone={riskTone(ctx.context_risk_level)}>{ctx.context_risk_level}</Badge>
                )}
              </div>
              {(ctx?.overall_context_summary || kyc?.aiKycContextSummary) && (
                <p className="text-muted-foreground">
                  {ctx?.overall_context_summary || kyc?.aiKycContextSummary}
                </p>
              )}
              {Array.isArray(ctx?.consistency_flags) && ctx.consistency_flags.length > 0 && (
                <div>
                  <div className="text-warning mb-1">Consistency flags</div>
                  <ul className="list-disc pl-4 text-muted-foreground">
                    {ctx.consistency_flags.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              )}
              {Array.isArray(ctx?.positive_signals) && ctx.positive_signals.length > 0 && (
                <div>
                  <div className="text-success mb-1">Positive signals</div>
                  <ul className="list-disc pl-4 text-muted-foreground">
                    {ctx.positive_signals.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              )}
              {Array.isArray(ctx?.questions_for_admin) && ctx.questions_for_admin.length > 0 && (
                <div>
                  <div className="text-gold mb-1">Questions for admin</div>
                  <ul className="list-disc pl-4 text-muted-foreground">
                    {ctx.questions_for_admin.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              )}
              {ctx?.salary_vs_limit_check && (
                <div>
                  <div className="text-muted-foreground mb-0.5">Salary vs limit</div>
                  <p className="text-foreground">{ctx.salary_vs_limit_check}</p>
                </div>
              )}
              {ctx?.nok_guarantor_notes && (
                <div>
                  <div className="text-muted-foreground mb-0.5">NOK / guarantor notes</div>
                  <p className="text-foreground">{ctx.nok_guarantor_notes}</p>
                </div>
              )}
            </div>
          )}

          {kyc && (
            <div className="mt-4 border border-border/60 rounded-md overflow-hidden">
              <button
                type="button"
                onClick={() => setDetailsOpen((o) => !o)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium hover:bg-accent/30"
              >
                Full KYC Details
                <span className="text-gold">{detailsOpen ? "−" : "+"}</span>
              </button>
              {detailsOpen && (
                <div className="px-3 pb-3 text-sm space-y-2 border-t border-border/60 pt-3">
                  {kyc.dateOfBirth && <DetailRow label="Date of birth" value={kyc.dateOfBirth} />}
                  {kyc.houseAddress && <DetailRow label="Home address" value={kyc.houseAddress} />}
                  {kyc.officeAddress && <DetailRow label="Office address" value={kyc.officeAddress} />}
                  {kyc.workplaceName && <DetailRow label="Workplace" value={kyc.workplaceName} />}
                  {kyc.salaryAmount != null && <DetailRow label="Salary" value={formatNaira(kyc.salaryAmount)} />}
                  {socials.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Socials</div>
                      {socials.map((s) => (
                        <DetailRow key={s.label} label={s.label} value={s.value} />
                      ))}
                    </div>
                  )}
                  {kyc.loanPurpose && <DetailRow label="Loan purpose" value={kyc.loanPurpose} />}
                  {kyc.repaymentPlan && <DetailRow label="Repayment plan" value={kyc.repaymentPlan} />}
                  {(kyc.nokName || kyc.nokPhone) && (
                    <div className="pt-1">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Next of kin</div>
                      {kyc.nokName && <DetailRow label="Name" value={kyc.nokName} />}
                      {kyc.nokRelationship && <DetailRow label="Relationship" value={kyc.nokRelationship} />}
                      {kyc.nokPhone && <DetailRow label="Phone" value={kyc.nokPhone} />}
                      {kyc.nokAddress && <DetailRow label="Address" value={kyc.nokAddress} />}
                    </div>
                  )}
                  {(kyc.guarantorName || kyc.guarantorPhone) && (
                    <div className="pt-1">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Guarantor</div>
                      {kyc.guarantorName && <DetailRow label="Name" value={kyc.guarantorName} />}
                      {kyc.guarantorRelationship && <DetailRow label="Relationship" value={kyc.guarantorRelationship} />}
                      {kyc.guarantorPhone && <DetailRow label="Phone" value={kyc.guarantorPhone} />}
                      {kyc.guarantorAddress && <DetailRow label="Address" value={kyc.guarantorAddress} />}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-4 flex gap-2 flex-wrap items-center">
            <Badge tone={kycTone(borrower.kycStatus)}>Status: {borrower.kycStatus.replace("_", " ")}</Badge>
            {isVerifiedUpgradePending(borrower) && (
              <Badge tone="warning">Applied to upgrade to Verified</Badge>
            )}
            {isSuper && borrower.kycStatus === "pending" && (
              <>
                <select
                  value={approveLevel}
                  onChange={(e) => setApproveLevel(e.target.value === "verified" ? "verified" : "express")}
                  className="bg-input/60 border border-border rounded-md px-2 py-1.5 text-xs"
                >
                  <option value="express">Approve as {TIER_LABEL.express}</option>
                  <option value="verified">Approve as {TIER_LABEL.verified}</option>
                </select>
                <Button variant="gold" loading={busy} onClick={() => setStatus("approved")}>
                  {isVerifiedUpgradePending(borrower) ? "Approve upgrade" : "Approve KYC"}
                </Button>
                <Button variant="danger" loading={busy} onClick={() => setStatus("declined")}>Decline</Button>
              </>
            )}
          </div>
          {isSuper && borrower.kycStatus === "pending" && (
            <div className="mt-3">
              <Field label="Decline reason (optional)">
                <Input value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="Reason shown to borrower" />
              </Field>
            </div>
          )}
          {!isSuper && <p className="mt-3 text-xs text-muted-foreground">Manager role can review but cannot approve/decline.</p>}
        </Card>

        {(borrower.kycStatus === "approved" || borrower.verificationLevel !== "none") && (
          <>
            {isSuper && borrower.kycStatus === "approved" && (
              <Card>
                <h3 className="font-display text-lg">Membership</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Change tier (demote/promote) or unapprove to revoke borrowing access. Docs stay on file.
                </p>
                <div className="mt-3 flex flex-wrap gap-2 items-end">
                  <Field label="Tier">
                    <select
                      value={membershipDraft}
                      onChange={(e) => setMembershipDraft(e.target.value as VerificationLevel)}
                      className="w-full bg-input/60 border border-border rounded-md px-3 py-2.5 text-sm"
                    >
                      <option value="express">{TIER_LABEL.express}</option>
                      <option value="verified">{TIER_LABEL.verified}</option>
                      <option value="elite">{TIER_LABEL.elite}</option>
                      <option value="none">Unapproved (none)</option>
                    </select>
                  </Field>
                  {membershipDraft === "none" && (
                    <Field label="Revoke reason (optional)">
                      <Input
                        value={revokeReason}
                        onChange={(e) => setRevokeReason(e.target.value)}
                        placeholder="Shown to borrower"
                      />
                    </Field>
                  )}
                  <Button
                    variant={membershipDraft === "none" ? "danger" : "gold"}
                    loading={busy}
                    onClick={async () => {
                      setBusy(true);
                      setError(null);
                      try {
                        await setBorrowerMembership(borrower.id, membershipDraft, {
                          kycId: borrower.kycId,
                          reason: revokeReason,
                        });
                        await onChange();
                      } catch (e) {
                        setError(e instanceof Error ? e.message : "Failed to update membership.");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {membershipDraft === "none" ? "Unapprove" : "Save membership"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Current:{" "}
                  {borrower.verificationLevel !== "none"
                    ? TIER_LABEL[borrower.verificationLevel]
                    : "None"}
                  {borrower.kycStatus === "approved" ? " · KYC approved" : ` · KYC ${borrower.kycStatus}`}
                </p>
              </Card>
            )}

            {isSuper && (borrower.verificationLevel === "verified" || isEliteReady(borrower)) && borrower.verificationLevel !== "elite" && (
              <Card>
                <h3 className="font-display text-lg">Elite membership</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {isEliteReady(borrower)
                    ? "Credit score suggests this Verified member is Elite-ready. Confirm to grant Elite."
                    : "Grant Elite for higher limits (up to ₦10m) and maximum negotiate flexibility."}
                </p>
                <Button
                  className="mt-3"
                  variant="gold"
                  loading={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError(null);
                    try {
                      await grantElite(borrower.id);
                      await onChange();
                    } catch (e) {
                      setError(e instanceof Error ? e.message : "Failed to grant Elite.");
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Grant Elite
                </Button>
              </Card>
            )}

            <Card>
              <h3 className="font-display text-lg">Loan limit</h3>
              <div className="mt-3 flex gap-2">
                <Input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} disabled={!isSuper} />
                <Button variant="gold" onClick={saveLimit} disabled={!isSuper} loading={busy}>Save</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Current: {formatNaira(borrower.loanLimit)}</p>
            </Card>

            <Card>
              <h3 className="font-display text-lg">Loan terms</h3>
              <p className="text-xs text-muted-foreground mt-1">Per-borrower overrides. Express uses weekly bands; Verified/Elite propose repay in naira.</p>

              <div className="mt-4 space-y-4">
                <div className="rounded-md border border-border p-3 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-medium">{TIER_LABEL.express} weekly schedule</div>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        checked={customExpressSchedule}
                        disabled={!isSuper}
                        onChange={(e) => setCustomExpressSchedule(e.target.checked)}
                      />
                      Custom for this borrower
                    </label>
                  </div>
                  <ExpressRateScheduleEditor
                    bands={expressScheduleDraft}
                    onChange={setExpressScheduleDraft}
                    disabled={!isSuper || !customExpressSchedule}
                  />
                  {!customExpressSchedule && (
                    <p className="text-xs text-muted-foreground">Using platform default schedule.</p>
                  )}
                </div>

                <Field label={`${TIER_LABEL.verified} internal guide rate (%)`} hint="Admin reference only — not shown to borrowers.">
                  <Input
                    type="number"
                    value={String(terms.verifiedGuideRate)}
                    disabled={!isSuper}
                    onChange={(e) => setTerms((t) => ({ ...t, verifiedGuideRate: Number(e.target.value) }))}
                  />
                </Field>

                <div className="flex items-center justify-between p-3 rounded-md border border-border bg-accent/30">
                  <div className="text-sm">Allow Verified / Elite borrowers to propose repayment</div>
                  <button
                    disabled={!isSuper}
                    onClick={() => setTerms((t) => ({ ...t, allowNegotiate: !t.allowNegotiate }))}
                    className={`relative h-6 w-11 rounded-full ${terms.allowNegotiate ? "bg-gradient-gold" : "bg-border"}`}
                  >
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-card transition ${terms.allowNegotiate ? "left-5" : "left-0.5"}`} />
                  </button>
                </div>

                <Button variant="gold" onClick={saveTerms} disabled={!isSuper} loading={busy}>Save terms</Button>
              </div>
            </Card>
          </>
        )}

        <Card>
          <h3 className="font-display text-lg">Loan history ({loans.length})</h3>
          {loans.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No loans yet.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {loans.map((l) => (
                <div key={l.id} className="flex justify-between text-sm py-2 border-b border-border/40">
                  <div>
                    <div>{formatNaira(l.amount)} · {formatDurationDays(l.durationDays)} · repay {formatNaira(l.proposedRepay ?? l.amount * (1 + l.interestRate / 100))}</div>
                    <div className="text-xs text-muted-foreground">{new Date(l.createdAt).toLocaleDateString()}</div>
                  </div>
                  <Badge tone={l.status === "active" ? "gold" : l.status === "repaid" ? "success" : l.status === "overdue" || l.status === "declined" ? "danger" : "neutral"}>
                    {l.status.replace("_", " ")}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-right break-words">{value}</span>
    </div>
  );
}

function DocPreview({
  doc,
  signed,
}: {
  doc: KycDocument;
  signed?: { url: string | null; error?: string };
}) {
  const label = KYC_DOC_LABELS[doc.type] || doc.type.replace(/_/g, " ");
  const url = signed?.url ?? null;
  const err = signed?.error;
  const isImage = KYC_IMAGE_TYPES.has(doc.type) || /\.(jpe?g|png|webp|gif)$/i.test(doc.fileName);
  const isPdf = KYC_PDF_TYPES.has(doc.type) || /\.pdf$/i.test(doc.fileName);

  return (
    <div className="rounded-md border border-border/60 p-2 space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      {url && isImage ? (
        <a href={url} target="_blank" rel="noreferrer" className="block">
          <img src={url} alt={label} className="w-full h-28 object-cover rounded-md border border-border/40" />
        </a>
      ) : url && isPdf ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center w-full rounded-md border border-gold/40 text-gold text-xs py-2 hover:bg-gold/10"
        >
          Open PDF
        </a>
      ) : url ? (
        <a href={url} target="_blank" rel="noreferrer" className="text-xs text-gold hover:underline break-all">
          Open file
        </a>
      ) : (
        <div className="text-xs text-muted-foreground break-all">{doc.fileName || "Document"}</div>
      )}
      {err && <div className="text-[10px] text-destructive">Preview unavailable</div>}
    </div>
  );
}
